import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { fetchOAuth, OAuthError } from '@/lib/apps-oauth';
import { useAuthStore } from '@/stores/auth-store';

/**
 * OAuth install/consent screen.
 *
 * An external app redirects the user here (via its /install or /oauth/start
 * endpoint) with the standard authorize params: client_id, redirect_uri,
 * scope, state, tenant. We call GET /apps/authorize to validate the
 * params + fetch the app's display metadata, render the consent UI, and
 * POST /apps/authorize on approval to get a redirect URL back to the
 * app's callback with an authorization code.
 *
 * Routed OUTSIDE AdminLayout on purpose: customers (role=user) arrive here
 * mid-checkout from the Site Builder, and the admin-only layout gate was
 * bouncing them to the landing page. This page guards sign-in itself
 * (preserving the full OAuth query through /login), and the backend
 * enforces role rules: admins install, users may only consent to an app an
 * admin already installed.
 */

const SCOPE_DESCRIPTIONS: Record<string, { label: string; detail: string }> = {
  read_shop: {
    label: 'Read tenant info',
    detail: 'Name, domain, currency, and other non-sensitive tenant settings.',
  },
  read_users: {
    label: 'Read user accounts',
    detail: "User ids, names, emails, and roles — so the app can recognize who's using it.",
  },
  read_subscriptions: {
    label: 'Read subscription status',
    detail: 'Plan + status for each user, so the app can gate features behind a paid plan.',
  },
  write_webhooks: {
    label: 'Register webhooks',
    detail: 'Create and remove event subscriptions (user.created, subscription.canceled, etc.).',
  },
  'ai:read': {
    label: 'Read AI configuration',
    detail: 'Fetch the tenant-configured AI provider credentials so the app can run AI calls against your keys instead of bringing its own.',
  },
  'ai:write': {
    label: 'Call AI on your behalf',
    detail: 'Issue AI inference, streaming, and image-generation calls through Rumio against your tenant-configured providers.',
  },
  write_users: {
    label: 'Manage user accounts',
    detail: 'Create and update user records on this tenant.',
  },
  read_strings: {
    label: 'Read site text',
    detail: 'Fetch translated UI strings and content snippets.',
  },
  read_sites: {
    label: 'Read site status',
    detail: 'Check the provisioning status of sites created through the app.',
  },
  write_sites: {
    label: 'Create sites',
    detail: 'Start the site-creation wizard on your behalf (e.g. building your store from a questionnaire).',
  },
  read_billing: {
    label: 'Read billing status',
    detail: 'See your Rumio hosting plan, trial status, and invoices — never your card details.',
  },
  write_billing: {
    label: 'Manage billing',
    detail: 'Open Stripe Checkout and the billing portal for your Rumio subscription. Payment details stay with Stripe.',
  },
  read_app_products: {
    label: 'Read its own product catalog',
    detail: "List the app's own purchasable products and prices.",
  },
  write_app_products: {
    label: 'Manage its own product catalog',
    detail: "Create and update the app's own purchasable products.",
  },
  read_app_purchases: {
    label: 'Read its own purchases',
    detail: 'See purchases users made inside this app (not your store orders).',
  },
  write_app_purchases: {
    label: 'Record its own purchases',
    detail: 'Start checkouts and record purchases for items sold inside this app.',
  },
};

/**
 * Map an OAuth `error` code from the backend into a sentence a tenant
 * admin can act on. The backend already echoes a short `error_description`
 * (e.g. "redirect_uri not allowed") — we surface that under the friendly
 * text for support / debugging, but lead with what they need to do.
 */
function explainOAuthError(
  code: string | null,
  description: string | null,
  ctx: { clientId: string; redirectUri: string; scope: string },
): string {
  switch (code) {
    case 'invalid_client':
      return `The app (client_id "${ctx.clientId}") isn't registered or has been disabled. Check the Apps list — if the app was deleted, re-register it; if it's disabled, re-enable it.`;
    case 'invalid_request':
      if (description?.includes('redirect_uri')) {
        return `The redirect URL "${ctx.redirectUri}" isn't on this app's allowlist. Edit the app on the Apps page and add this URL to its redirect URIs, then try installing again.`;
      }
      if (description?.includes('tenant')) {
        return `This install link was generated for a different tenant. Go back to the Apps page and click Install from this tenant.`;
      }
      return `The install request is missing required information. Go back and click Install again — if this keeps happening, the app's registration is incomplete.`;
    case 'invalid_scope':
      if (description?.includes('Unknown scope')) {
        return `The install link is requesting a scope Rumio doesn't recognize. The app likely needs an update.`;
      }
      return `The install link is asking for permissions that aren't allowed for this app. Edit the app on the Apps page and add the requested scopes (currently registered: see the Scopes column).`;
    case 'admin_required':
      return (
        description ||
        "This app needs a site administrator's approval before you can use it. Contact the site owner."
      );
    case 'request_failed':
      return description || 'Something went wrong contacting the server. Try again in a moment.';
    default:
      return description || 'The install request was rejected.';
  }
}

interface ConsentInfo {
  app: {
    client_id: string;
    name: string;
    description: string | null;
    icon_url: string | null;
    /** First-party (Rumio/partner) apps auto-approve — no consent card. */
    first_party?: boolean;
  };
  tenant: string;
  redirect_uri: string;
  scopes: string[];
  state: string | null;
}

export default function AppsAuthorize() {
  const { user, isInitialized } = useAuthStore();
  const location = useLocation();
  const [params] = useSearchParams();
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const scope = params.get('scope') ?? '';
  const state = params.get('state');
  const tenant = params.get('tenant') ?? '';

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorDescription, setErrorDescription] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  const query = useMemo(() => {
    const qs = new URLSearchParams();
    if (clientId) qs.set('client_id', clientId);
    if (redirectUri) qs.set('redirect_uri', redirectUri);
    if (scope) qs.set('scope', scope);
    if (state) qs.set('state', state);
    if (tenant) qs.set('tenant', tenant);
    return qs.toString();
  }, [clientId, redirectUri, scope, state, tenant]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setErrorCode(null);
        setErrorDescription(null);
        const res = (await fetchOAuth(`/apps/authorize?${query}`)) as ConsentInfo;
        if (!cancelled) setInfo(res);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof OAuthError) {
          setErrorCode(e.code);
          setErrorDescription(e.description);
        } else {
          setErrorCode('request_failed');
          setErrorDescription(e instanceof Error ? e.message : 'Failed to load consent info');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [query]);

  // First-party (Rumio/partner) apps skip the consent card: approve as soon
  // as the consent info validates and hand the browser straight to the app's
  // callback. Ref-guarded so re-renders can't double-fire; a failed
  // auto-approve falls through to the normal error card.
  const autoApproved = useRef(false);
  useEffect(() => {
    if (info?.app.first_party && !autoApproved.current) {
      autoApproved.current = true;
      void handleApprove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  async function handleApprove() {
    if (!info) return;
    try {
      setApproving(true);
      const res = (await fetchOAuth('/apps/authorize', {
        method: 'POST',
        body: JSON.stringify({
          client_id: info.app.client_id,
          redirect_uri: info.redirect_uri,
          scope: info.scopes.join(' '),
          state: info.state ?? undefined,
          tenant: info.tenant,
        }),
      })) as { redirect_to: string };
      // Hand the browser back to the app's callback with the code.
      window.location.assign(res.redirect_to);
    } catch (e) {
      if (e instanceof OAuthError) {
        setErrorCode(e.code);
        setErrorDescription(e.description);
      } else {
        setErrorCode('request_failed');
        setErrorDescription(e instanceof Error ? e.message : 'Failed to approve install');
      }
      setApproving(false);
    }
  }

  function handleDeny() {
    if (!redirectUri) return;
    const sep = redirectUri.includes('?') ? '&' : '?';
    const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';
    window.location.assign(`${redirectUri}${sep}error=access_denied${stateParam}`);
  }

  // Sign-in guard (this page renders outside AdminLayout). Preserve the full
  // OAuth query through /login so the consent flow resumes here after auth.
  if (isInitialized && !user) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  if (!isInitialized || loading) {
    return (
      <div className="mx-auto max-w-xl py-12">
        <Card>
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        </Card>
      </div>
    );
  }

  if (errorCode || !info) {
    return (
      <div className="mx-auto max-w-xl py-12">
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h1 className="text-lg font-semibold">Can't install this app</h1>
              <p className="text-sm text-stone-600 mt-1">
                {explainOAuthError(errorCode, errorDescription, { clientId, redirectUri, scope })}
              </p>
              {errorDescription && errorCode && (
                <p className="text-xs text-stone-500 mt-2 font-mono">
                  {errorCode}: {errorDescription}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            {user?.role === 'admin' ? (
              <Link
                to="/admin/apps"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to apps
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => window.history.back()}
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Go back
              </button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // First-party apps never render the consent card — just a connecting state
  // while the auto-approve above round-trips and redirects.
  if (info.app.first_party) {
    return (
      <div className="mx-auto max-w-xl py-12">
        <Card>
          <div className="py-8 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            <p className="text-sm text-stone-600">Connecting to {info.app.name}…</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl py-12">
      <Card>
        <div className="flex items-start gap-4 mb-6">
          {info.app.icon_url ? (
            <img src={info.app.icon_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-sage-100 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-sage-700" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-semibold">{info.app.name}</h1>
            {info.app.description && (
              <p className="text-sm text-stone-600 mt-1">{info.app.description}</p>
            )}
            <p className="text-xs text-stone-500 mt-2">
              wants to install on <span className="font-mono">{info.tenant}</span>
            </p>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-sm font-semibold text-stone-700 mb-2">It will be able to:</h2>
          <ul className="space-y-2">
            {info.scopes.map((s) => {
              const desc = SCOPE_DESCRIPTIONS[s];
              return (
                <li key={s} className="flex items-start gap-2 text-sm">
                  <ShieldCheck className="w-4 h-4 text-sage-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">{desc?.label ?? s}</div>
                    <div className="text-stone-600">{desc?.detail ?? s}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <p className="text-xs text-stone-500 mb-4">
          The app will receive an access token scoped to this tenant. You can revoke it anytime from{' '}
          <span className="font-mono">/admin/apps</span>.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={handleDeny} disabled={approving}>
            Cancel
          </Button>
          <Button onClick={handleApprove} disabled={approving}>
            {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve & Install'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
